/**
 * Escape's owner, and the one place that knows what pausing actually means.
 *
 * Escape used to leave the table outright: one keypress took a hand in
 * progress straight to the main menu, with nothing to confirm it and — online
 * — with the room socket left behind. It now opens `ui/PauseMenu`, and leaving
 * is a deliberate second click.
 *
 * Kept in `scenes/` with the rest of the composition glue because the three
 * facts it has to hold are all composition facts: free play and a lesson are
 * both "the game scene" (they share the canvas, and `main.ts`'s route value
 * `game` covers both), only the free-play table can genuinely be frozen (see
 * `SceneDriver.setPaused`), and leaving an online table means dropping the room
 * as well as the scene.
 *
 * The pause card stacks *over* whatever else is on screen — `.modal-overlay`
 * outranks the hand-end modal and the online overlay, and `installFocusTrap`
 * is stack-aware — so Escape does the same thing at every moment of a hand
 * rather than being conditionally swallowed.
 */

import type { SettingsRow } from '@bn-games/ui-shell';
import { PauseMenu } from '../ui/PauseMenu.js';
import type { GameScene } from './GameScene.js';

export interface PauseHostDeps {
  readonly gameScene: GameScene;
  /** Live rows for the in-modal settings view — same builder the menu uses. */
  readonly settingsRows: () => readonly SettingsRow[];
  /** True while the canvas (free play or a lesson) is the mounted scene. */
  readonly inGame: () => boolean;
  /** Leave for the menu, dropping any online room on the way out. */
  readonly quit: () => void;
}

export class PauseHost {
  private readonly menu: PauseMenu;

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    // Outside the game the menu screens own Escape (a room code field closes
    // itself with it, for one) — never steal it from them.
    if (!this.menu.isOpen() && !this.deps.inGame()) return;
    e.preventDefault();
    this.toggle();
  };

  constructor(private readonly deps: PauseHostDeps) {
    this.menu = new PauseMenu({
      onResume: () => this.deps.gameScene.setPaused(false),
      onQuit: () => {
        // Un-pause first: `quit` unmounts the scene, and a driver disposed
        // mid-pause would leave the next hand's bot loop frozen.
        this.deps.gameScene.setPaused(false);
        this.deps.quit();
      },
      getSettingsRows: () => this.deps.settingsRows(),
    });
    window.addEventListener('keydown', this.onKeyDown);
  }

  toggle(): void {
    if (this.menu.isOpen()) {
      this.menu.close();
      this.deps.gameScene.setPaused(false);
      return;
    }
    this.deps.gameScene.setPaused(true);
    this.menu.open();
  }

  /** Close the card without resuming anything — for a scene that is going away
   *  under it (a match ending, the online table being left from its overlay). */
  dismiss(): void {
    this.menu.close();
  }

  /** Something outside the card changed the settings it is displaying. */
  refresh(): void {
    this.menu.refresh();
  }
}
